import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
(() => {
  function init(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const strip = root.querySelector('.cw-marquee');
    let paused = reduced.matches, hovering = false, visible = false, direction = 1, previous = 0, raf;
    const videos = [...strip.querySelectorAll('video')];
    const syncVideo = () => videos.forEach(video => {
      const a = video.getBoundingClientRect(), b = strip.getBoundingClientRect();
      if (visible && !paused && a.right > b.left && a.left < b.right) video.play().catch(() => {});
      else video.pause();
    });
    strip.addEventListener('pointerenter', () => { hovering = true; });
    strip.addEventListener('pointerleave', () => { hovering = false; });
    strip.addEventListener('focusin', () => { hovering = true; });
    strip.addEventListener('focusout', () => { hovering = strip.matches(':hover'); });
    strip.addEventListener('pointerdown', () => { paused = true; });
    strip.addEventListener('keydown', event => {
      if (event.key === ' ' && event.target === strip) {
        event.preventDefault(); paused = !paused; syncVideo();
      }
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault(); paused = true;
        strip.scrollBy({left: (event.key === 'ArrowRight' ? 1 : -1) * 280, behavior: reduced.matches ? 'instant' : 'smooth'});
      }
    });
    const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; syncVideo(); }, {threshold:.05});
    observer.observe(strip);
    strip.addEventListener('scroll', () => { if (!paused) syncVideo(); }, {passive:true});
    function frame(now) {
      if (!root.isConnected) { observer.disconnect(); return; }
      const elapsed = Math.min(now - (previous || now), 50); previous = now;
      if (visible && !paused && !hovering && !document.hidden) {
        const end = strip.scrollWidth - strip.clientWidth;
        if (end > 1) {
          strip.scrollLeft += direction * elapsed * .025;
          if (strip.scrollLeft >= end - 1) direction = -1;
          if (strip.scrollLeft <= 0) direction = 1;
        }
      }
      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);
    reduced.addEventListener('change', () => { paused = reduced.matches; syncVideo(); });
    document.addEventListener('visibilitychange', () => { if (document.hidden) videos.forEach(v => v.pause()); else syncVideo(); });
    const dialog = root.querySelector('dialog');
    let opener;
    root.querySelectorAll('[data-lightbox]').forEach(link => link.addEventListener('click', event => {
      event.preventDefault(); opener = link;
      const img = dialog.querySelector('img'); img.src = link.href; img.alt = link.querySelector('img').alt;
      dialog.showModal();
    }));
    dialog.querySelector('button').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
    dialog.addEventListener('close', () => opener?.focus());
    const cartPath = (window.Shopify?.routes?.root || '/') + 'cart';
    const sectionId = root.id.replace('CharacterWorld-', '');
    let busy = false;
    let refreshing;
    function updateVariant() {
      const cart = root.querySelector('.cw-cart');
      const form = cart.querySelector('.cw-buy-form');
      if (!form) return;
      const option = form.querySelector('[name=id]').selectedOptions[0];
      const quantity = form.querySelector('[name=quantity]');
      const button = form.querySelector('[type=submit]');
      const stock = Number(option.dataset.stock);
      const remaining = stock < 0 ? -1 : Math.max(0, stock - Number(option.dataset.inCart));
      const available = option.dataset.available === 'true';
      quantity.removeAttribute('max');
      if (remaining >= 0) quantity.max = String(remaining);
      quantity.disabled = !available || remaining === 0 || busy;
      button.disabled = !available || remaining === 0 || busy;
      button.textContent = available ? cart.dataset.addLabel : cart.dataset.soldLabel;
      root.querySelector('[data-variant-price]').textContent = option.dataset.price;
    }
    function applyCart(html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const next = parsed.querySelector('.cw-cart');
      if (!next) throw new Error(root.querySelector('[data-cart-status]').dataset.syncError);
      const current = root.querySelector('.cw-cart');
      const selected = current.querySelector('[name=id]')?.value;
      const quantity = current.querySelector('[name=quantity]')?.value;
      current.replaceWith(next);
      const select = next.querySelector('[name=id]');
      if (select && [...select.options].some(o => o.value === selected)) select.value = selected;
      if (quantity && next.querySelector('[name=quantity]')) next.querySelector('[name=quantity]').value = quantity;
      updateVariant();
    }
    async function refreshCart() {
      if (refreshing) return refreshing;
      refreshing = (async () => {
        const url = new URL(location.href);
        url.searchParams.set('section_id', sectionId);
        const response = await fetch(url, {cache:'no-store'});
        if (!response.ok) throw new Error(root.querySelector('[data-cart-status]').dataset.syncError);
        applyCart(await response.text());
      })();
      try { await refreshing; } finally { refreshing = null; }
    }
    root.addEventListener('change', event => {
      if (event.target.matches('.cw-buy-form [name=id]')) updateVariant();
    });
    root.addEventListener('submit', async event => {
      if (!event.target.matches('.cw-buy-form')) return;
      event.preventDefault();
      if (busy || !event.target.reportValidity()) return;
      const requestedVariant = event.target.querySelector('[name=id]').value;
      const requestedQuantity = event.target.querySelector('[name=quantity]').value;
      busy = true;
      updateVariant();
      let added = false;
      let deferred;
      const labels = {...root.querySelector('[data-cart-status]').dataset};
      try {
        await refreshCart();
        const form = root.querySelector('.cw-buy-form');
        form.querySelector('[name=id]').value = requestedVariant;
        form.querySelector('[name=quantity]').value = requestedQuantity;
        busy = false; updateVariant();
        if (form.querySelector('[type=submit]').disabled || !form.reportValidity()) return;
        const body = new FormData(form);
        const drawerSections = [...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId);
        body.set('sections', [...new Set([sectionId,...drawerSections])].slice(0,5).join(','));
        body.set('sections_url', location.pathname + location.search);
        busy = true; updateVariant();
        form.setAttribute('aria-busy','true');
        root.querySelector('[data-cart-status]').textContent = '';
        deferred = CartLinesUpdateEvent.createPromise();
        root.dispatchEvent(new CartLinesUpdateEvent({
          action:'add', context:'product',
          lines:[{merchandiseId:requestedVariant,quantity:Number(requestedQuantity)}],
          promise:deferred.promise
        }));
        const response = await fetch(cartPath + '/add.js', {method:'POST',headers:{Accept:'application/json'},body});
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || labels.error);
        added = true;
        const cartResponse = await fetch(cartPath + '.js', {cache:'no-store'});
        if (!cartResponse.ok) throw new Error(labels.error);
        const cart = await cartResponse.json();
        deferred.resolve({
          cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail:{items:cart.items,itemCount:cart.item_count,sections:data.sections,source:'character-world',didError:false}
        });
        deferred = null;
        if (data.sections?.[sectionId]) applyCart(data.sections[sectionId]);
        else await refreshCart();
        root.querySelector('[data-cart-status]').textContent = labels.added;
      } catch(error) {
        deferred?.reject(error);
        if (!added) {
          try { await refreshCart(); } catch {}
        }
        root.querySelector('[data-cart-status]').textContent = added ? labels.addedRefresh : (error.message || labels.error);
      } finally {
        busy = false;
        root.querySelector('.cw-buy-form')?.removeAttribute('aria-busy');
        updateVariant();
      }
    });
    document.addEventListener(StandardEvents.cartLinesUpdate, event => {
      if (event.target === root) return;
      event.promise?.then(() => { if (root.isConnected && !busy) return refreshCart(); }).catch(() => {});
    });
    window.addEventListener('pageshow', event => {
      if (event.persisted && root.isConnected) refreshCart().catch(() => {});
    });
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && root.isConnected && !busy) refreshCart().catch(() => {});
    });
    updateVariant();
  }
  document.querySelectorAll('[data-character-world]').forEach(init);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-character-world]').forEach(init));
})();
